import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  input,
  output,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { TrimDirective } from '@plugins/client/directives/trim.directive';
import { ClearInputComponent } from '@plugins/client/forms/clear-input/clear-input.component';
import {
  FORM_BASE_CONTROL_CMP,
  FormBaseControlComponent,
  FormBaseControlStyleDirective,
  setControlRelationship,
} from '@plugins/client/forms/form';
import { Maybe } from '@plugins/shared/types';

type InputType = 'text' | 'search' | 'email' | 'url' | 'tel' | 'password';

@Component({
  selector: 'plugins-text-input',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TrimDirective,
    ClearInputComponent,
    FormBaseControlStyleDirective,
  ],
  templateUrl: './text-input.component.html',
  styleUrl: './text-input.component.scss',
  providers: [
    {
      provide: FORM_BASE_CONTROL_CMP,
      // eslint-disable-next-line @angular-eslint/no-forward-ref
      useExisting: forwardRef(() => TextInputComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextInputComponent extends FormBaseControlComponent {
  @Input() type: InputType = 'text';
  @Input() autocomplete: 'on' | 'off' = 'off';
  @Input() ariaAutocomplete?: string;
  @Input() ariaControls?: string;
  @Input() ariaExpanded?: boolean;
  @Input() ariaActivedescendant?: string;
  @Input() placeholder = '';
  @Input() reset = false;
  @Input() resetLabel?: string;

  readonly readonly = input<'readonly' | null, Maybe<boolean>>(null, {
    transform: (val) => (val ? 'readonly' : null),
  });

  readonly keyPressed = output<KeyboardEvent>();

  override setControlRelationship() {
    setControlRelationship({
      controlDirective: this.controlDirective,
      formControl: this.control,
      destroy$: this.destroy$,
      onChange: this.onChange,
      emitEvent: true,
      changeDetectorRef: this.cdRef,
      writeValueToParent: true,
      inheritValidators: true,
    });
  }

  onReset(): void {
    this.control.setValue('');
    this.focus();
  }

  handleKeydown(event: KeyboardEvent) {
    this.keyPressed.emit(event);
  }
}
